<template>
  <b-row>

    <!-- ══ SELF SCOPE: Personal Pipeline Funnel ══════════════════════ -->
    <template v-if="scope === 'self'">

      <!-- Pipeline funnel -->
      <b-col md="7" class="mb-2 d-flex">
        <b-card no-body class="w-100 mb-0">
          <b-card-header class="d-flex align-items-center justify-content-between pb-75 pt-1 px-2">
            <div>
              <h5 class="mb-0 font-weight-bold">My Recruitment Pipeline</h5>
              <small class="text-muted">Count at each hiring stage for the selected period</small>
            </div>
          </b-card-header>
          <b-card-body class="px-1 pb-1 pt-50">
            <vue-apex-charts
              v-if="selfPipelineHasData"
              type="bar"
              height="260"
              :options="selfPipelineOptions"
              :series="selfPipelineSeries"
            />
            <div
              v-else
              class="d-flex align-items-center justify-content-center text-muted"
              style="height:260px"
            >
              No data for the selected period
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Radial conversion gauge -->
      <b-col md="5" class="mb-2 d-flex">
        <b-card no-body class="w-100 mb-0">
          <b-card-header class="d-flex align-items-center justify-content-between pb-75 pt-1 px-2">
            <div>
              <h5 class="mb-0 font-weight-bold">My Conversion Rate</h5>
              <small class="text-muted">Profiles Shared → Joiners</small>
            </div>
          </b-card-header>
          <b-card-body class="d-flex flex-column align-items-center justify-content-between py-1 px-2">
            <!-- Radial gauge -->
            <vue-apex-charts
              type="radialBar"
              height="200"
              :options="selfRadialOptions"
              :series="selfRadialSeries"
              class="w-100"
            />
            <!-- Stage breakdown pills -->
            <div class="d-flex flex-wrap justify-content-center border-top pt-75 w-100">
              <span
                v-for="st in selfStages"
                :key="st.label"
                class="d-flex align-items-center mr-75 mb-50"
              >
                <span
                  class="rounded-circle mr-25"
                  :class="`bg-${st.color}`"
                  style="width:8px;height:8px;display:inline-block"
                />
                <span class="small text-muted">{{ st.label }}:</span>
                <strong class="small ml-25">{{ st.value }}</strong>
              </span>
            </div>
          </b-card-body>
        </b-card>
      </b-col>

    </template>

    <!-- ══ TEAM / ORG SCOPE: Top Recruiters bar + trend ══════════════ -->
    <template v-else>

      <!-- Bar: Top recruiters -->
      <b-col md="7" class="mb-2">
        <b-card no-body>
          <b-card-header class="d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h5 class="mb-0">Top Recruiters</h5>
            <b-form-select
              v-model="chartMetric"
              :options="chartMetricOptions"
              size="sm"
              style="width:130px"
            />
          </b-card-header>
          <b-card-body class="px-1 pb-1 pt-50">
            <vue-apex-charts
              v-if="barSeries[0].data.length"
              type="bar"
              height="280"
              :options="barOptions"
              :series="barSeries"
            />
            <div
              v-else
              class="d-flex align-items-center justify-content-center text-muted"
              style="height:280px"
            >
              No data for the selected period
            </div>
          </b-card-body>
        </b-card>
      </b-col>

    </template>

    <!-- Line: Monthly trend (team/org scope only — self gets the trend below the funnel row) -->
    <b-col v-if="scope !== 'self'" md="5" class="mb-2">
      <b-card no-body>
        <b-card-header class="pb-0">
          <h5 class="mb-0">Monthly Trend</h5>
        </b-card-header>
        <b-card-body class="px-1 pb-1 pt-50">
          <vue-apex-charts
            v-if="trendSeries[0].data.length"
            type="line"
            height="280"
            :options="trendOptions"
            :series="trendSeries"
          />
          <div
            v-else
            class="d-flex align-items-center justify-content-center text-muted"
            style="height:280px"
          >
            No data for the selected period
          </div>
        </b-card-body>
      </b-card>
    </b-col>

    <!-- Self scope: My Monthly Trend (full width, below funnel row) -->
    <b-col v-if="scope === 'self'" cols="12" class="mb-2">
      <b-card no-body>
        <b-card-header class="pb-0">
          <h5 class="mb-0">My Monthly Trend</h5>
          <small class="text-muted">Joiners, Offered and Drops over time</small>
        </b-card-header>
        <b-card-body class="px-1 pb-1 pt-50">
          <vue-apex-charts
            v-if="trendSeries[0].data.length"
            type="line"
            height="200"
            :options="trendOptions"
            :series="trendSeries"
          />
          <div
            v-else
            class="d-flex align-items-center justify-content-center text-muted"
            style="height:200px"
          >
            No data for the selected period
          </div>
        </b-card-body>
      </b-card>
    </b-col>

  </b-row>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { BRow, BCol, BCard, BCardBody, BCardHeader, BFormSelect } from 'bootstrap-vue'
import { formatRevenue } from '../perf-utils'

export default {
  name: 'PerfCharts',
  components: { VueApexCharts, BRow, BCol, BCard, BCardBody, BCardHeader, BFormSelect },

  props: {
    filteredRows: { type: Array, default: () => [] },
    filteredRaw:  { type: Array, default: () => [] },
    scope:        { type: String, default: 'org' },  // 'self' | 'team' | 'org'
  },

  data() {
    return {
      chartMetric: 'joiners_count',
      chartMetricOptions: [
        { value: 'joiners_count',     text: 'Joiners' },
        { value: 'offered_count',     text: 'Offered' },
        { value: 'shortlisted_count', text: 'Shortlisted' },
        { value: 'total_revenue',     text: 'Revenue' },
        { value: 'drops_count',       text: 'Drops' },
      ],
    }
  },

  computed: {
    // ── SELF SCOPE: aggregate totals across all rows for this recruiter ──
    selfTotals() {
      const rows = this.filteredRaw
      return {
        ps:      rows.reduce((s, r) => s + (+r.profile_shared_count || 0), 0),
        sl:      rows.reduce((s, r) => s + (+r.shortlisted_count    || 0), 0),
        offered: rows.reduce((s, r) => s + (+r.offered_count        || 0), 0),
        joined:  rows.reduce((s, r) => s + (+r.joiners_count        || 0), 0),
        drops:   rows.reduce((s, r) => s + (+r.drops_count         || 0), 0),
      }
    },

    selfStages() {
      const t = this.selfTotals
      return [
        { label: 'Profiles Shared', value: t.ps,      color: 'primary' },
        { label: 'Shortlisted',     value: t.sl,      color: 'info'    },
        { label: 'Offered',         value: t.offered, color: 'warning' },
        { label: 'Joined',          value: t.joined,  color: 'success' },
        { label: 'Drops',           value: t.drops,   color: 'danger'  },
      ]
    },

    selfPipelineHasData() {
      const t = this.selfTotals
      return (t.ps + t.sl + t.offered + t.joined) > 0
    },

    selfPipelineSeries() {
      const t = this.selfTotals
      return [{
        name: 'Count',
        data: [
          { x: 'Profile Shared', y: t.ps,      fillColor: '#7367f0' },
          { x: 'Shortlisted',    y: t.sl,      fillColor: '#00cfe8' },
          { x: 'Offered',        y: t.offered, fillColor: '#ff9f43' },
          { x: 'Joined',         y: t.joined,  fillColor: '#28c76f' },
          { x: 'Drops',          y: t.drops,   fillColor: '#ea5455' },
        ],
      }]
    },

    selfPipelineOptions() {
      const max = Math.max(...this.selfPipelineSeries[0].data.map(d => d.y), 1)
      return {
        chart:       { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true, barHeight: '55%' } },
        dataLabels:  {
          enabled: true,
          style:   { fontSize: '13px', fontWeight: 700, colors: ['#fff'] },
          formatter: v => v === 0 ? '' : String(v),
        },
        legend: { show: false },
        colors: ['#7367f0', '#00cfe8', '#ff9f43', '#28c76f', '#ea5455'],
        xaxis:  {
          min: 0,
          max,
          tickAmount: Math.min(max, 5),
          labels: {
            style:     { fontSize: '11px' },
            formatter: v => Number.isInteger(v) ? String(v) : '',
          },
        },
        yaxis: {
          labels: { style: { fontSize: '12px', fontWeight: 600 } },
        },
        grid:    { borderColor: '#ebe9f1', xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
        tooltip: { y: { formatter: v => String(v) } },
      }
    },

    // Radial gauge: PS → Joined conversion %
    selfRadialSeries() {
      const { ps, joined } = this.selfTotals
      const pct = ps > 0 ? Math.round((joined / ps) * 100) : 0
      return [pct]
    },

    selfRadialOptions() {
      const pct = this.selfRadialSeries[0]
      const color = pct >= 20 ? '#28c76f' : pct >= 10 ? '#ff9f43' : '#ea5455'
      return {
        chart:       { type: 'radialBar', fontFamily: 'inherit', toolbar: { show: false }, sparkline: { enabled: false } },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle:    135,
            hollow:      { size: '65%', background: 'transparent' },
            track:       { background: '#ebe9f1', strokeWidth: '97%', margin: 5 },
            dataLabels:  {
              name:  {
                show:     true,
                fontSize: '12px',
                color:    '#6e6b7b',
                offsetY:  -8,
              },
              value: {
                show:      true,
                fontSize:  '26px',
                fontWeight: 700,
                color:     color,
                offsetY:   5,
                formatter: v => `${v}%`,
              },
            },
          },
        },
        colors: [color],
        labels: ['Conversion'],
        stroke: { lineCap: 'round' },
      }
    },

    // ── TEAM / ORG SCOPE: Top recruiters bar (collapsed across verticals) ──
    barSeries() {
      const metric = this.chartMetric

      // Aggregate per recruiter (emp_id), summing across verticals
      const byRecruiter = {}
      this.filteredRows.forEach(r => {
        const id = r.emp_id || r.recruiter_name
        if (!byRecruiter[id]) {
          byRecruiter[id] = { name: r.recruiter_name || r.emp_id, total: 0 }
        }
        byRecruiter[id].total += +r[metric] || 0
      })

      const sorted = Object.values(byRecruiter)
        .sort((a, b) => b.total - a.total)
        .slice(0, 15)

      return [{
        name: this.chartMetricOptions.find(o => o.value === metric)?.text || metric,
        data: sorted.map(r => ({
          x: r.name,
          y: metric === 'total_revenue' ? Math.round(r.total / 1000) : r.total,
        })),
      }]
    },

    barOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { columnWidth: '50%', borderRadius: 4 } },
        dataLabels: { enabled: true, style: { fontSize: '10px', fontWeight: 600 } },
        colors: ['#7367f0'],
        xaxis: { labels: { style: { fontSize: '10px' } } },
        yaxis: {
          labels: {
            formatter: v => this.chartMetric === 'total_revenue' ? `${v}k` : String(v),
          },
        },
        tooltip: {
          y: {
            formatter: v => this.chartMetric === 'total_revenue'
              ? formatRevenue(v * 1000)
              : String(v),
          },
        },
        grid: { borderColor: '#ebe9f1' },
      }
    },

    // ── Monthly trend (all scopes) ────────────────────────────────────
    trendSeries() {
      const monthly = {}
      this.filteredRaw.forEach(r => {
        if (!monthly[r.month]) monthly[r.month] = { offered: 0, joiners: 0, drops: 0 }
        monthly[r.month].offered += +r.offered_count || 0
        monthly[r.month].joiners += +r.joiners_count || 0
        monthly[r.month].drops   += +r.drops_count   || 0
      })
      const months = Object.keys(monthly).sort()
      const mk     = k => months.map(m => ({ x: `${m}-01`, y: monthly[m][k] }))
      return [
        { name: 'Joiners', data: mk('joiners') },
        { name: 'Offered', data: mk('offered') },
        { name: 'Drops',   data: mk('drops')   },
      ]
    },

    trendOptions() {
      return {
        chart: { type: 'line', toolbar: { show: false }, fontFamily: 'inherit', zoom: { enabled: false } },
        stroke: { curve: 'smooth', width: 2 },
        colors: ['#28c76f', '#7367f0', '#ea5455'],
        markers: { size: 3 },
        xaxis: { type: 'datetime', labels: { format: 'MMM yy', style: { fontSize: '10px' } } },
        yaxis: { labels: { style: { fontSize: '10px' } } },
        legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px' },
        grid: { borderColor: '#ebe9f1' },
        tooltip: { x: { format: 'MMM yyyy' } },
      }
    },
  },
}
</script>
